import React from "react"
import { Tooltip } from "antd"
import { SwapOutlined, DragOutlined, LinkOutlined } from "@ant-design/icons"
import Strings from "utils/strings"
import { RouteAddress } from "../../types"
import { ChangeType } from "./types"
import {
	GROUPED_COLOR,
	MOVED_COLOR,
	REPOSITIONED_COLOR,
	TOOLTIP_COLOR,
	formatClock,
} from "./utils"

// Static, non-draggable counterpart to Pill.tsx — position comes from the
// parent row (sequence-based), not from this component.
const ReadOnlyPill: React.FC<{
	addr: RouteAddress
	left: number
	eta?: number
	color: string
	sequence: number
	changeType?: ChangeType
	// Where this stop ranked before the proposal, shown as "was→now".
	previousSequence?: number
	previousRouteName?: string
	// Recalculated time this stop's PICKUP group is collected, if any.
	pickupEta?: number
	// "Atual" row: a plain gray snapshot, not the route's own color.
	muted?: boolean
	// Lights up just this stop's connector line while hovered.
	onHover?: (hovering: boolean) => void
}> = ({
	addr,
	left,
	eta,
	color,
	sequence,
	changeType = "none",
	previousSequence,
	previousRouteName,
	pickupEta,
	muted = false,
	onHover,
}) => {
	const order = addr.order
	const bundledOrderIds = order.bundledOrders?.map((o) => o.orderId) ?? []
	const isGrouped = bundledOrderIds.length > 0
	const isChanged = changeType !== "none"
	const changeBadgeColor =
		changeType === "moved" ? MOVED_COLOR : REPOSITIONED_COLOR
	// Change badge owns the corner when both apply — it's the more
	// actionable of the two.
	const cornerColor = isChanged ? changeBadgeColor : GROUPED_COLOR
	const sequenceLabel =
		isChanged && previousSequence != null
			? `${previousSequence}→${sequence}`
			: `${sequence}`

	const tooltipContent = (
		<div style={{ minWidth: 200, maxWidth: 280 }}>
			<div style={{ fontWeight: 600, marginBottom: 4 }}>
				({sequenceLabel}) {order.orderId}
			</div>
			{addr.address && <div style={{ color: "#bfbfbf" }}>{addr.address}</div>}
			<div style={{ marginTop: 4 }}>{formatClock(eta)}</div>
			{pickupEta != null && (
				<div style={{ marginTop: 4, color: "#b37feb" }}>
					{Strings.formatString(
						Strings.routes.moveOrdersPickupCollectedAt,
						formatClock(pickupEta)
					)}
				</div>
			)}
			{changeType === "moved" && previousRouteName && (
				<div style={{ marginTop: 4, color: "#ffc069" }}>
					{Strings.formatString(
						Strings.routes.moveOrdersOptimizeMovedFrom,
						previousSequence != null
							? `${previousRouteName} (${previousSequence})`
							: previousRouteName
					)}
				</div>
			)}
			{isGrouped && (
				<>
					<div
						style={{
							display: "flex",
							alignItems: "center",
							gap: 6,
							fontSize: 11.5,
							fontWeight: 650,
							color: "#2dd4bf",
							marginTop: 8,
							marginBottom: 6,
						}}
					>
						<LinkOutlined />
						{Strings.formatString(
							Strings.routes.moveOrdersGroupedWithCount,
							bundledOrderIds.length
						)}
					</div>
					<div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
						{bundledOrderIds.map((id) => (
							<span
								key={id}
								style={{
									// Overrides the flex column's default stretch, which would
									// otherwise fill this short id to the tooltip's full width.
									alignSelf: "flex-start",
									boxSizing: "border-box",
									maxWidth: "100%",
									fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
									fontSize: 11.5,
									background: "rgba(255,255,255,0.08)",
									border: "1px solid rgba(255,255,255,0.12)",
									borderRadius: 5,
									padding: "3px 8px",
									color: "#d7f5ef",
								}}
							>
								{id}
							</span>
						))}
					</div>
				</>
			)}
		</div>
	)

	return (
		<Tooltip title={tooltipContent} color={TOOLTIP_COLOR}>
			<div
				onMouseEnter={() => onHover?.(true)}
				onMouseLeave={() => onHover?.(false)}
				style={{
					position: "absolute",
					left: `${left}%`,
					top: "50%",
					transform: "translate(-50%, -50%)",
					display: "inline-flex",
					alignItems: "center",
					justifyContent: "center",
					height: 26,
					minWidth: 26,
					boxSizing: "border-box",
					background: muted ? "#fff" : color,
					color: muted ? "#6b7280" : "#fff",
					borderRadius: 7,
					border:
						isChanged || isGrouped
							? `1.5px solid ${cornerColor}`
							: muted
								? "1px solid #d8dbe0"
								: "none",
					padding: "0 5px",
					fontSize: 10.5,
					fontWeight: 700,
					fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
					whiteSpace: "nowrap",
				}}
			>
				{sequenceLabel}
				{/* Top-right corner: whichever matters more when both apply — a
					change in sequence/route (icon) over just being grouped (+N). */}
				{isChanged ? (
					<span
						style={{
							position: "absolute",
							top: -7,
							right: -7,
							width: 14,
							height: 14,
							borderRadius: "50%",
							background: changeBadgeColor,
							border: "1px solid #fff",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
						}}
					>
						{changeType === "moved" ? (
							<SwapOutlined style={{ color: "#fff", fontSize: 8 }} />
						) : (
							<DragOutlined style={{ color: "#fff", fontSize: 8 }} />
						)}
					</span>
				) : (
					isGrouped && (
						<span
							style={{
								position: "absolute",
								top: -8,
								right: -8,
								minWidth: 16,
								height: 16,
								padding: "0 4px",
								borderRadius: 8,
								background: GROUPED_COLOR,
								border: "1px solid #fff",
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								fontSize: 9,
								fontWeight: 700,
								color: "#fff",
								fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
							}}
						>
							+{bundledOrderIds.length}
						</span>
					)
				)}
				{/* Rare case: a stop that's both changed and grouped — the change
					badge already took the top-right corner, so this falls back to
					the bottom-left one rather than disappearing entirely. */}
				{isChanged && isGrouped && (
					<span
						style={{
							position: "absolute",
							bottom: -7,
							left: -7,
							minWidth: 16,
							height: 16,
							padding: "0 4px",
							borderRadius: 8,
							background: GROUPED_COLOR,
							border: "1px solid #fff",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							fontSize: 9,
							fontWeight: 700,
							color: "#fff",
							fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
						}}
					>
						+{bundledOrderIds.length}
					</span>
				)}
			</div>
		</Tooltip>
	)
}

export default ReadOnlyPill
